/**
 * The exception-led line under a work section's heading.
 *
 * Three clauses at most, in one order: what is going wrong, how much there is,
 * and where it comes from — "3 overdue · 9 open · plan activities and actions,
 * due date order".
 *
 * EACH CLAUSE RENDERS ONLY WHEN ITS OWN FIGURE ARRIVED, the same discipline
 * HomeTiles applies to deltas. The server omits a delta key entirely for a user
 * who may not see it, so a missing figure narrows the line rather than blanking
 * the heading or printing an "undefined overdue".
 *
 * A CLAUSE MAY ONLY NAME A FILTER THE NUMBER ACTUALLY WENT THROUGH. Open
 * actions says "open" because that count really is open-filtered — plan
 * activities that are not Complete, plus actions with no completed date. The
 * four R.A.D.I.O. counts have no status filter at all: status titles are
 * event-configurable free text with no open/closed flag behind them, and an
 * opportunity has no status column whatsoever. Each of those lines therefore
 * names the record type — "5 risks", "4 issues", "3 opportunities" — because
 * calling any of them "open" would describe a filter the number was never put
 * through.
 *
 * The three procurement sections each answer to that same rule:
 *
 *  - EVALUATIONS SAYS "OUTSTANDING" because the count genuinely is
 *    status-filtered — an evaluated template is excluded from it server-side.
 *  - CONTRACTS SAYS "ACTIVE" for the same reason, and reads it straight off
 *    `count`. HomeWorkScope::contracts() excludes closed contracts, so the
 *    count IS the active figure — there is no separate `contracts_active`
 *    delta, and there must not be one. (An earlier version of this section did
 *    carry that delta, because the count then included closed contracts and
 *    calling it "active" would have been the exact mislabelling this rule
 *    forbids. Excluding them server-side removed the need rather than the
 *    honesty requirement.)
 *  - PROCUREMENTS NAMES THE RECORD TYPE. Its count has no filter on it at all.
 */
export const TYPE_OPEN_ACTIONS = 'open_actions';
export const TYPE_RISKS = 'risks';
export const TYPE_ISSUES = 'issues';
export const TYPE_DECISIONS = 'decisions';
export const TYPE_OPPORTUNITIES = 'opportunities';
export const TYPE_PROCUREMENTS = 'procurements';
export const TYPE_CONTRACTS = 'contracts';
export const TYPE_EVALUATIONS = 'evaluations';

/**
 * The "Your items" tiles, in the fixed order they are shown in.
 *
 * SIX OF THE EIGHT WORK TYPES. Opportunities and evaluations have no tile:
 * neither is work the owner is being asked to do — an opportunity has no status
 * and no date to be late against, and an evaluation is waiting on its scorers —
 * so on a row of six they were the two that only ever added width.
 *
 * ONLY THE TILES AND THE PANEL GO. TYPE_OPPORTUNITIES and TYPE_EVALUATIONS are
 * still real work types with server sections, row DTOs, section tables and
 * titles behind them, reachable from their own modules' own screens. Neither
 * is reachable from THIS panel today — no tile, attention card or
 * default-open pick can set `openType` to a value this array doesn't name, so
 * HomeSections' table entries for both are dead code on the client until a
 * type is added back here. Do not delete the row DTOs, section tables or
 * entries below because this list no longer names them — restoring a tile is
 * meant to be a one-line change to this array.
 */
export const SECTION_TYPES = [TYPE_PROCUREMENTS, TYPE_CONTRACTS, TYPE_OPEN_ACTIONS, TYPE_RISKS, TYPE_ISSUES, TYPE_DECISIONS];

const figure = (value) => (typeof value === 'number' ? value : null);

export const sectionTitle = (type) =>
    ({
        /* Named for the records it holds — plan activities and the actions hung
           off them — rather than for the state they are in. "My open actions"
           described the filter and left the user to work out which module it
           came from. */
        [TYPE_OPEN_ACTIONS]: $t('My project plan activities'),
        [TYPE_RISKS]: $t('My risks'),
        [TYPE_ISSUES]: $t('My issues'),
        [TYPE_DECISIONS]: $t('My decisions'),
        [TYPE_OPPORTUNITIES]: $t('My opportunities'),
        [TYPE_PROCUREMENTS]: $t('My procurements'),
        [TYPE_CONTRACTS]: $t('My contracts'),
        [TYPE_EVALUATIONS]: $t('My evaluations'),
    })[type];

/** The module a section's rows live in, named as the left navigation names it. */
export const sectionModule = (type) => {
    if (type === TYPE_OPEN_ACTIONS) {
        return $t('Project plan');
    }

    /* All three procurement sections live under one navigation entry, so they
       all name it — the sub-modules gating them are not places a user browses
       to. */
    if (type === TYPE_PROCUREMENTS || type === TYPE_CONTRACTS || type === TYPE_EVALUATIONS) {
        return $t('Procurement');
    }

    return $t('R.A.D.I.O.');
};

/**
 * The figure that makes this section an exception, already worded — or null
 * when the section has none, or when the figure did not arrive.
 */
export const sectionException = (type, deltas = {}) => {
    if (type === TYPE_OPEN_ACTIONS) {
        const overdue = figure(deltas.actions_overdue);

        return overdue > 0 ? $t('{{count}} overdue', { count: overdue }) : null;
    }

    if (type === TYPE_RISKS) {
        const red = figure(deltas.risks_red);

        return red > 0 ? $t('{{count}} red', { count: red }) : null;
    }

    if (type === TYPE_ISSUES || type === TYPE_DECISIONS) {
        const high = figure(type === TYPE_ISSUES ? deltas.issues_high_priority : deltas.decisions_high_priority);

        return high > 0 ? $t('{{count}} high priority', { count: high }) : null;
    }

    if (type === TYPE_PROCUREMENTS) {
        const overdue = figure(deltas.procurements_overdue_to_market);

        return overdue > 0 ? $t('{{count}} overdue to market', { count: overdue }) : null;
    }

    if (type === TYPE_CONTRACTS) {
        const unsigned = figure(deltas.contracts_unsigned);

        return unsigned > 0 ? $t('{{count}} unsigned', { count: unsigned }) : null;
    }

    if (type === TYPE_EVALUATIONS) {
        /* Raised and never sent to its scorers — waiting on the owner and on
           nobody else, which is what makes it the exception rather than merely
           the earlier of two statuses. */
        const notSent = figure(deltas.evaluations_not_sent);

        return notSent > 0 ? $t('{{count}} not sent', { count: notSent }) : null;
    }

    /* Opportunities carry no exception figure — they have no status and no due
       date to be late against, so there is nothing here that can go wrong. */
    return null;
};

/**
 * What the panel says when the user genuinely holds none of this kind.
 *
 * NOT THE SAME SENTENCE AS THE STALE-COUNT ONE, and the two must not be merged.
 * A tile now renders at zero, so "you have none of these" is an ordinary,
 * expected answer that the user asked for by clicking the tile — it names the
 * record type and stops. "These items are no longer available" says something
 * else entirely: the count arrived with the page, the rows arrived after it,
 * and something was deleted in between, so what is on screen is out of date and
 * refreshing will fix it. Telling a user with no decisions to refresh sends
 * them to reload a page that was right the first time.
 */
export const sectionEmptyLabel = (type) =>
    ({
        [TYPE_OPEN_ACTIONS]: $t('No project plan activities are assigned to you.'),
        [TYPE_RISKS]: $t('No risks are assigned to you.'),
        [TYPE_ISSUES]: $t('No issues are assigned to you.'),
        [TYPE_DECISIONS]: $t('No decisions are assigned to you.'),
        [TYPE_OPPORTUNITIES]: $t('No opportunities are assigned to you.'),
        [TYPE_PROCUREMENTS]: $t('No procurements are assigned to you.'),
        [TYPE_CONTRACTS]: $t('No contracts are assigned to you.'),
        [TYPE_EVALUATIONS]: $t('No evaluations are assigned to you.'),
    })[type];

/*
 * Two explicit keys per type rather than i18next pluralisation: `count` is
 * reserved there and would look for a plural suffix on the key instead of
 * substituting the value (see RuleRow.js and PeopleLevelStructure.js for the
 * same pattern). A section landed on every morning is not a rare place to see
 * exactly one row, so "1 risks" is worth the second key.
 */
const VOLUME_SINGULAR = {
    [TYPE_OPEN_ACTIONS]: () => $t('1 open'),
    [TYPE_RISKS]: () => $t('1 risk'),
    [TYPE_ISSUES]: () => $t('1 issue'),
    [TYPE_DECISIONS]: () => $t('1 decision'),
    [TYPE_OPPORTUNITIES]: () => $t('1 opportunity'),
    [TYPE_PROCUREMENTS]: () => $t('1 procurement'),
    [TYPE_CONTRACTS]: () => $t('1 active'),
    [TYPE_EVALUATIONS]: () => $t('1 outstanding'),
};

const VOLUME_PLURAL = {
    [TYPE_OPEN_ACTIONS]: (count) => $t('{{count}} open', { count }),
    [TYPE_RISKS]: (count) => $t('{{count}} risks', { count }),
    [TYPE_ISSUES]: (count) => $t('{{count}} issues', { count }),
    [TYPE_DECISIONS]: (count) => $t('{{count}} decisions', { count }),
    [TYPE_OPPORTUNITIES]: (count) => $t('{{count}} opportunities', { count }),
    [TYPE_PROCUREMENTS]: (count) => $t('{{count}} procurements', { count }),
    [TYPE_CONTRACTS]: (count) => $t('{{count}} active', { count }),
    [TYPE_EVALUATIONS]: (count) => $t('{{count}} outstanding', { count }),
};

/**
 * How much work the section holds, named the way its figure was actually
 * measured — see the note at the top of this file.
 */
const volumeClause = (type, count) => (count === 1 ? VOLUME_SINGULAR[type]() : VOLUME_PLURAL[type](count));

const sourceClause = (type) => (type === TYPE_OPEN_ACTIONS ? $t('plan activities and actions, due date order') : sectionModule(type));

/**
 * @returns {{exception: string|null, detail: string}} the exception clause, which
 *   the caller styles as one, and the rest of the line already joined.
 */
export const sectionSummary = (type, count, deltas) => ({
    exception: sectionException(type, deltas),
    detail: [volumeClause(type, count), sourceClause(type)].join(' · '),
});
